import { IPQuestion, IDryRunAnswerData } from "modules/organization/models/interface";
import DryRunCalculateEquation, { IAllEquationSet, IDryRunStepNode } from "./dryRunCalculateEquation";

// Client-side port of mn-api-helpers/src/modules/transform/calculateResult.ts's ordering: every
// non-IMPORT rank's equation can be evaluated in any order (none of them depend on another
// rank's result), then IMPORT ranks are evaluated in display_order DESCENDING order so that,
// however deep an IMPORT chain goes (e.g. Gold -> Silver -> Bronze), each rank's own possible
// IMPORT targets have already been resolved by the time its turn comes. Unlike the original,
// this classifies "contains IMPORT" with a single case-insensitive check rather than the
// server's always-true `!includes('IMPORT') || !includes('import')` — same final numbers,
// without the redundant first-pass throw-and-catch on IMPORT equations.
const containsImport = (equation: string) => equation.toUpperCase().includes("IMPORT");

export const resolveAllRankResults = (
  equationSet: IAllEquationSet[],
  questions: IPQuestion[],
  answers: IDryRunAnswerData[],
): IAllEquationSet[] => {
  const nonImportRanks = equationSet.filter((eachEq) => !containsImport(eachEq.equation));
  const importRanks = equationSet
    .filter((eachEq) => containsImport(eachEq.equation))
    .sort((a, b) => (b.display_order ?? 0) - (a.display_order ?? 0));

  [...nonImportRanks, ...importRanks].forEach((eachEq) => {
    try {
      const calculateObj = new DryRunCalculateEquation(
        eachEq.equation,
        questions,
        answers,
        equationSet,
        eachEq.display_order,
      );
      eachEq.result = calculateObj.getResult() as 0 | 1;
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.warn(`Dry Run: rank '${eachEq.name}' equation failed to evaluate: ${error?.message}`);
      eachEq.result = 0;
    }
  });

  return equationSet;
};

export interface IDryRunResult {
  result: number;
  tree: IDryRunStepNode | null;
  error?: string;
}

// Top-level entry point for the Dry Run modal. `currentRankName`/`liveEquationText` are the rank
// actually open in the editor (uses its unsaved buffer); every other rank uses its last-saved
// equation text (`savedEquations`), per the user's explicit call — Dry Run should preview an
// unsaved edit, but treat every other rank as ground truth.
export const runDryRun = (
  currentRankName: string,
  liveEquationText: string,
  ranks: string[],
  allEquations: { [rankName: string]: { display_order?: number | null } },
  savedEquations: { [rankName: string]: string },
  questions: IPQuestion[],
  answers: IDryRunAnswerData[],
): IDryRunResult => {
  try {
    const equationSet: IAllEquationSet[] = ranks.map((rankName) => ({
      name: rankName,
      equation:
        rankName === currentRankName ? liveEquationText : savedEquations[rankName] ?? "",
      result: null,
      display_order: allEquations[rankName]?.display_order,
    }));

    resolveAllRankResults(equationSet, questions, answers);

    const currentEntry = equationSet.find((each) => each.name === currentRankName);
    if (!currentEntry) {
      return { result: 0, tree: null, error: `Rank '${currentRankName}' not found.` };
    }

    const visitedRanks = new Set<string>([currentRankName]);

    const buildImportedTree = (rankName: string): IDryRunStepNode | null => {
      if (visitedRanks.has(rankName)) {
        return null;
      }
      const targetEntry = equationSet.find((each) => each.name === rankName);
      if (!targetEntry) {
        return null;
      }
      visitedRanks.add(rankName);
      try {
        const calc = new DryRunCalculateEquation(
          targetEntry.equation,
          questions,
          answers,
          equationSet,
          targetEntry.display_order,
        );
        return calc.getResultWithSteps(buildImportedTree).tree;
      } catch (error: any) {
        return { label: rankName, kind: "operator", result: targetEntry.result ?? 0, detail: error?.message };
      } finally {
        visitedRanks.delete(rankName);
      }
    };

    const calculateObj = new DryRunCalculateEquation(
      currentEntry.equation,
      questions,
      answers,
      equationSet,
      currentEntry.display_order,
    );
    const { result, tree } = calculateObj.getResultWithSteps(buildImportedTree);
    return { result, tree };
  } catch (error: any) {
    return { result: 0, tree: null, error: error?.message ?? "Dry Run failed to evaluate this equation." };
  }
};
